// Sales · Renewals & Churn — accounts grouped by churn probability (work the
// left columns first). Data: /analytics/churn (license_analytics) + /analytics/mrr.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { PageHeader, Card, Btn } from '@/components/shared/primitives';
import { ProgressBar } from '@/components/shared/Kpi';
import { AlertBanner } from '@/components/shared/AlertBanner';
import { moneyK } from '@/lib/format';
import { getChurn, getMrrAnalytics, type LicenseAnalyticsRow, type MrrRow } from '@/lib/api/sales';

const COLS: Array<[string, string, string]> = [
  ['critical', 'Critical', 'var(--churn-critical)'], ['high', 'High', 'var(--churn-high)'], ['medium', 'Medium', 'var(--churn-medium)'], ['low', 'Low', 'var(--churn-low)'],
];

export function SalesRenewals() {
  const navigate = useNavigate();
  const [churn, setChurn] = useState<LicenseAnalyticsRow[]>([]);
  const [mrrByLic, setMrrByLic] = useState<Map<string, MrrRow>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([getChurn(), getMrrAnalytics().catch(() => null)])
      .then(([c, m]) => {
        setChurn(c);
        const map = new Map<string, MrrRow>();
        (m?.rows || []).forEach((r) => map.set(r.license_id, r));
        setMrrByLic(map);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load churn data'))
      .finally(() => setLoading(false));
  }, []);

  const board = useMemo(() => {
    const b: Record<string, LicenseAnalyticsRow[]> = { critical: [], high: [], medium: [], low: [] };
    churn.forEach((c) => { const lvl = c.churn_risk_level || 'low'; (b[lvl] || b.low).push(c); });
    Object.values(b).forEach((arr) => arr.sort((a, z) => (z.churn_probability || 0) - (a.churn_probability || 0)));
    return b;
  }, [churn]);

  return (
    <div>
      <PageHeader title="Renewals & Churn" subtitle="Accounts grouped by churn probability — work the left columns first" actions={<Btn icon="download" disabled title="Renewals export is not yet supported.">Export board</Btn>} />
      {error && <div style={{ marginBottom: 16 }}><AlertBanner kind="error">{error}</AlertBanner></div>}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
          {COLS.map(([key, label, color]) => (
            <div key={key}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 4px 10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}><span style={{ width: 9, height: 9, borderRadius: '50%', background: color }} /><span style={{ fontSize: 13, fontWeight: 700 }}>{label}</span></div>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>{board[key].length}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {board[key].map((a) => {
                  const r = mrrByLic.get(a.license_id);
                  const name = a.license?.organization?.name || r?.customer_name || 'Unknown';
                  const pct = Math.round((a.churn_probability || 0) * (a.churn_probability && a.churn_probability <= 1 ? 100 : 1));
                  return (
                    <Card key={a.license_id} pad="13px 14px" hover onClick={() => navigate(`/sales/licenses/${a.license_id}`)} style={{ borderTop: `2px solid ${color}` }}>
                      <div style={{ fontWeight: 600, fontSize: 13.5, marginBottom: 8 }}>{name}</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
                        <span>{r ? `${moneyK(r.total_mrr)}/mo` : '—'}</span>
                      </div>
                      <ProgressBar pct={pct} color={color} height={4} />
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>{pct}% churn probability</div>
                    </Card>
                  );
                })}
                {board[key].length === 0 && <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: '18px 0' }}>None</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
export default SalesRenewals;
